// Writing to the payments ledger from OUTSIDE the Payments screen (the IPC tab
// issuing a certificate, the Contract tab generating advance certificates).
//
// The ledger has two copies: the browser's local cache and, once sync is on,
// the cloud workspace. The Payments screen pulls the cloud copy when it opens
// and replaces the cache with it — so anything written only to the cache is
// silently lost the moment the ledger is viewed. Every outside write therefore
// goes through applyToLedger: it applies the change to the cloud copy first
// (when there is one), pushes it, and only then refreshes the cache.
import type { PaymentsData } from "../payments/types";
import { loadPayments, savePayments } from "../payments/store";
import { getRemote, getWorkspace, putRemote } from "./ledgerSync";

export interface LedgerWriteResult {
  /** The change reached the cloud ledger (or no cloud ledger is configured). */
  synced: boolean;
  /** Why it did not reach the cloud, when it didn't. */
  error?: string;
}

/** Apply `mutate` to the authoritative ledger copy. The mutator is applied to
 * one copy at a time (re-applied only on a save conflict), so it may create ids. */
export async function applyToLedger(mutate: (d: PaymentsData) => void): Promise<LedgerWriteResult> {
  const ws = getWorkspace();
  if (ws) {
    try {
      const r = await getRemote(ws);
      if (r.exists && r.data) {
        let data = r.data;
        mutate(data);
        let res = await putRemote(ws, data, r.version);
        if ("conflict" in res) {
          // Someone saved in between — re-apply onto their version and push again.
          data = res.data;
          mutate(data);
          res = await putRemote(ws, data, res.version);
          if ("conflict" in res) return { synced: false, error: "the ledger changed while saving — try again" };
        }
        savePayments(data);
        return { synced: true };
      }
      // Workspace exists but holds nothing yet: fall through and seed it from local.
    } catch (e: any) {
      // Offline or no access: keep the change locally; the Payments screen will
      // push the cache when it next syncs successfully.
      const local = loadPayments();
      mutate(local);
      savePayments(local);
      return { synced: false, error: e?.needLogin ? "sign in to sync" : e?.forbidden ? "no access to this ledger" : "offline — saved locally" };
    }
  }
  const local = loadPayments();
  mutate(local);
  savePayments(local);
  if (ws) {
    try { await putRemote(ws, local); return { synced: true }; } catch { return { synced: false, error: "offline — saved locally" }; }
  }
  return { synced: true };
}

/** Refresh the local cache from the cloud ledger (so screens that read the
 * cache — e.g. the IPC tab's contract picker — see the current contracts). */
export async function refreshLedgerCache(): Promise<PaymentsData> {
  const ws = getWorkspace();
  if (ws) {
    try {
      const r = await getRemote(ws);
      if (r.exists && r.data) { savePayments(r.data); return r.data; }
    } catch { /* offline — use the cache */ }
  }
  return loadPayments();
}
